/**
 * KMI PropertyScan — Terms & Conditions content.
 *
 * Source of truth for the in-app Terms modal. View-only: do not expose a
 * download/print affordance. Ported verbatim from the legacy UI's
 * `src/content/terms.js` so the modal renders identically.
 */

export const TERMS_TITLE   = 'KMI PropertyScan™ – Terms and Conditions';
export const TERMS_VERSION = 'v1';

export interface TermsSection {
    n:    number;
    h:    string;
    body: string | string[];
}

export const TERMS_SECTIONS: TermsSection[] = [
    {
        n: 1,
        h: 'Acceptance of Terms',
        body: 'By accessing KMI PropertyScan™, users agree to comply with these platform Terms & Conditions. PropertyScan is designed to provide QR-powered property engagement experiences and lead generation workflows for the real estate industry.',
    },
    {
        n: 2,
        h: 'Account Security and User Responsibilities',
        body: [
            'Security: Users are responsible for maintaining account security and ensuring accurate property information. Role-based permissions and secure authentication controls are utilized to protect platform access.',
            'Content Compliance: Users are solely responsible for ensuring all uploaded media and listing content comply with applicable laws, ownership rights, and fair housing regulations.',
        ],
    },
    {
        n: 3,
        h: 'Subscription Plans, Billing, and Payments',
        body: [
            'PropertyScan operates on a tiered subscription model offering Owner-side and Agent-side plans.',
            'Free Trial: A 7-day free trial is available on all paid plans, requiring no credit card to start.',
            'Billing Cycles: Subscriptions can be billed monthly or annually, with annual billing offering a 20% discount over the monthly rate. All subscription fees are non-refundable after the first 21 days.',
            'Plan Modifications: Plan upgrades take effect immediately with prorated charges applied. Downgrades take effect at the next renewal cycle.',
            'Usage Limits and Overages: Tenants who reach their plan limits will receive in-app warnings at 80% capacity. At 100% capacity, tenants are blocked from creating new resources and face a soft-block until the next billing cycle or until a plan upgrade is executed.',
            'Third-Party Fees: Twilio SMS costs are not included in the base plan price and are passed through to tenants based on actual usage.',
        ],
    },
    {
        n: 4,
        h: 'Cancellations and Data Export',
        body: [
            'Cancellation: Tenants may self-cancel their subscription at any time via the dashboard. Access to the platform will continue until the end of the current billing period.',
            'Data Export: Following cancellation, data export functionality will remain available to the tenant for 30 days.',
        ],
    },
    {
        n: 5,
        h: 'QR Code Usage and Restrictions',
        body: [
            'Management: QR codes may be activated, deactivated, reassigned, and tracked for analytics purposes within the platform.',
            'Prohibited Actions: Unauthorized duplication or misuse of QR codes is strictly prohibited. KMI PropertyScan™ reserves the right to suspend accounts or deactivate QR codes for any violations of platform policies.',
        ],
    },
    {
        n: 6,
        h: 'Intellectual Property',
        body: 'KMI retains all right, title, and interest in and to the PropertyScan platform, including all software, platform architecture, QR routing technology, and branding. Users retain all ownership rights to the media, images, and property data they upload to the platform.',
    },
    {
        n: 7,
        h: 'Privacy and Compliance',
        body: [
            'Data Processing: The platform collects buyer information (name, phone number, email, preferences) and technical data (IP address, QR scan activity) for lead routing, marketing, and platform improvements. This data may be shared with authorized builders, developers, brokers, and realtors.',
            'Consumer Rights: Users and prospective buyers may request access, correction, deletion, or opt-out of personal data processing in accordance with applicable US state privacy laws (such as CCPA).',
            "Communications (TCPA/SMS): By utilizing the platform's communication tools, users agree to adhere to TCPA/SMS and CAN-SPAM compliance practices.",
        ],
    },
    {
        n: 8,
        h: 'Limitation of Liability',
        body: "To the maximum extent permitted by law, KMI shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from the use or inability to use PropertyScan. In no event shall KMI's aggregate liability exceed the total amount paid by the user for the subscription in the twelve (12) months preceding the claim.",
    },
    {
        n: 9,
        h: 'Indemnification',
        body: "Users agree to indemnify, defend, and hold harmless KMI, its affiliates, and its officers from any claims, damages, liabilities, and expenses (including legal fees) arising from the user's violation of these terms, user-uploaded content, violation of fair housing regulations, or any disputes between the user and their respective real estate clients.",
    },
    {
        n: 10,
        h: 'Governing Law and Jurisdiction',
        body: 'These Terms and Conditions shall be governed by and construed in accordance with the laws of the State of Georgia, without regard to its conflict of law principles. Any legal action or proceeding arising out of or related to these Terms shall be brought exclusively in the state or federal courts located in Atlanta, Georgia.',
    },
];
